import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTrialBalance } from '../../hooks/useReports';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import type { DrillTarget } from '../../components/reports/DrilldownModal';
import { ExportButtons } from '../../components/reports/ExportButtons';
import { AsOfPicker } from '../../components/reports/PeriodFilter';
import { RefreshButton } from '../../components/reports/RefreshButton';
import { ActiveOnlyToggle, useActiveOnly } from '../../components/reports/ActiveOnlyToggle';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reports/trial-balance')({
  component: TrialBalancePage,
});

function TrialBalancePage() {
  const selection = useActiveSelection();
  const navigate = useNavigate();
  const [asOf, setAsOf] = useState(todayIso());
  const [activeOnly, setActiveOnly] = useActiveOnly();
  const { data, isLoading, error, refetch, isFetching } = useTrialBalance(selection, asOf, activeOnly);

  const openDrilldown = (target: DrillTarget): void =>
    void navigate({
      to: '/reports/drilldown',
      search: {
        codes: target.codes.join(','),
        label: target.label,
        to: asOf,
        back: '/reports/trial-balance',
      },
    });

  if (!selection) return <NoCompany />;

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← All reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Trial Balance
          {selection === CONSOLIDATED && <span className="tag" style={{ marginLeft: 8 }}>Consolidated</span>}
        </h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <AsOfPicker asOf={asOf} onChange={setAsOf} />
          <ActiveOnlyToggle value={activeOnly} onChange={setActiveOnly} />
          <RefreshButton onRefresh={() => void refetch()} isFetching={isFetching} />
          <ExportButtons selection={selection} type="trial_balance" params={activeOnly ? { asOf, activeOnly: 'true' } : { asOf }} />
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load report.</p>}

      {data && (
        <>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            As of {formatAccountingDate(data.asOf)}
            {!data.balanced && (
              <span className="tag bad" style={{ marginLeft: 8 }}>OUT OF BALANCE</span>
            )}
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Account</th>
                <th style={{ textAlign: 'right' }}>Debit</th>
                <th style={{ textAlign: 'right' }}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.accountId}>
                  <td>{r.code}</td>
                  <td>{r.name}</td>
                  <td style={{ textAlign: 'right' }}>
                    {r.debit === '0.00' ? '' : (
                      <button className="amount-link" onClick={() => openDrilldown({ codes: [r.code], label: `${r.code} — ${r.name}` })}>
                        {formatCurrency(r.debit)}
                      </button>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {r.credit === '0.00' ? '' : (
                      <button className="amount-link" onClick={() => openDrilldown({ codes: [r.code], label: `${r.code} — ${r.name}` })}>
                        {formatCurrency(r.credit)}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <td colSpan={2}>Total</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="amount-link"
                    style={{ fontWeight: 700 }}
                    onClick={() => openDrilldown({ codes: data.rows.map((r) => r.code), label: 'All accounts' })}
                  >
                    {formatCurrency(data.totals.debit)}
                  </button>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="amount-link"
                    style={{ fontWeight: 700 }}
                    onClick={() => openDrilldown({ codes: data.rows.map((r) => r.code), label: 'All accounts' })}
                  >
                    {formatCurrency(data.totals.credit)}
                  </button>
                </td>
              </tr>
            </tfoot>
          </table>
        </>
      )}
    </div>
  );
}

function NoCompany() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 32 }}>
      <p className="muted">No active company.</p>
      <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
        Go to Companies
      </Link>
    </div>
  );
}
